import './roomAvailability.scss'

import { type FC, useEffect } from 'react'
import { useParams } from 'react-router'

import { Spin } from 'antd'
import dayjs from 'dayjs'
import { useFormik } from 'formik'

import { Button } from 'common/components/Button/Button'
import { Modal } from 'common/components/Modal'
import Show from 'common/components/Show2/Show'
import { useAppDispatch } from 'common/hooks/redux'

import {
  useCreatePromotionRoomsBookingMutation,
  useLazyGetDayRoomsAvailabilityQuery,
} from 'features/Home/Promotion/state/promotionsApi'
import { updatePromotionLatestCrud } from 'features/Home/Promotion/state/promotionsSlice'
import type { RoomAvailabilityProps } from './roomAvailability.types'
import { RoomLoadingSkeleton } from './RoomLoadingSkeleton'
import { RoomBookingSlots } from './RoomBookingSlots'
import Popup from 'common/components/Popup'
import { validFlexibleRoomIds } from 'features/Home/Promotion/PromotionDetails/Rooms/Rooms'

export const RoomAvailability: FC<RoomAvailabilityProps> = ({
  date,
  conventionRooms,
  skeletonRooms = 1,
  open,
  handleCancel,
  isFlexibleRooms = false,
}) => {
  const dispatch = useAppDispatch()
  const { bookingId, siteId } = useParams()

  const [fetchRoomAvailability, { data, isFetching }] = useLazyGetDayRoomsAvailabilityQuery()

  const [createBooking] = useCreatePromotionRoomsBookingMutation()

  const { values, isSubmitting, setFieldValue, handleSubmit, resetForm } = useFormik({
    initialValues: {
      slots: {},
    },
    onSubmit,
    enableReinitialize: true,
  })

  function onCancel() {
    resetForm()
    handleCancel()
  }

  function getIsFlexibleStatus(roomId: number) {
    return isFlexibleRooms && validFlexibleRoomIds.includes(roomId)
  }

  async function onSubmit() {
    try {
      let totalFlexibleSlotsUsed = 0
      const weekdaySlots = isFlexibleRooms ? conventionRooms[0].weekday_slots : 0

      const data = Object.keys(values.slots).map((key) => {
        const roomId = Number(key)
        const conventionRoom = getConventionRoom(roomId)
        const selectedSlots = values.slots[key]
        const isFlexible = getIsFlexibleStatus(roomId)

        // Transform slots into object with slot type (0 for weekend, 1 for weekday)
        const slots = selectedSlots.reduce((acc, slotId) => {
          // For flexible rooms, use total slots used across all rooms
          // For non-flexible rooms, use room's own weekday slots
          const areAllSlotsUsed = isFlexible
            ? totalFlexibleSlotsUsed < weekdaySlots
            : Object.keys(acc).length < conventionRoom.weekday_slots

          const isWeekdaySlot = !isWeekend && areAllSlotsUsed

          if (isFlexible) totalFlexibleSlotsUsed++

          acc[slotId] = isWeekdaySlot ? 1 : 0
          return acc
        }, {})

        return {
          id: roomId,
          slots,
        }
      })

      const response = await createBooking({
        bookingId,
        siteId,
        date,
        data,
      })

      dispatch(updatePromotionLatestCrud(response.data))
      onCancel()
    } catch (error) {}
  }

  const handleSlotToggle = (roomId: number, slotId: number) => {
    const roomsSlots = values.slots[roomId] ?? []
    const prevSelectedSlotIds = new Set(roomsSlots)
    const isFlexible = getIsFlexibleStatus(roomId)

    if (prevSelectedSlotIds.has(slotId)) {
      // If removing a slot, just remove it
      prevSelectedSlotIds.delete(slotId)
    } else {
      // When adding a slot, check if we have reached max slots
      if (isFlexible) {
        // For flexible rooms, check against the total pool of flexible slots
        const totalAvailableSlots = getTotalAvailableSlotsForFlexibleRooms()
        const totalUsedSlots = getTotalUsedSlotsForFlexibleRooms()

        // Only add the new slot if we haven't reached the max
        if (totalUsedSlots < totalAvailableSlots) {
          prevSelectedSlotIds.add(slotId)
        }
      } else {
        // For non-flexible rooms, check against this room's max slots
        const conventionRoom = getConventionRoom(roomId)
        const weekendSlots = conventionRoom.weekend_slots
        const weekdaySlots = conventionRoom.weekday_slots

        // We allow to use weekend slots for weekday bookings, since weekend is more expensive
        const maximumSlots = isWeekend ? weekendSlots : weekdaySlots + weekendSlots

        // Only add if we haven't reached max slots for this room
        if (roomsSlots.length < maximumSlots) {
          prevSelectedSlotIds.add(slotId)
        }
      }
    }

    setFieldValue(`slots.${roomId}`, Array.from(prevSelectedSlotIds))
  }

  const handleGroupToggle = (roomId: number, groupSlotIds: number[]) => {
    const alreadySelected = values.slots[roomId] ?? []
    const hasAnySelected = groupSlotIds.some((slotId) => alreadySelected.includes(slotId))
    const isFlexible = getIsFlexibleStatus(roomId)

    let finalSelectedSlots: number[]

    if (hasAnySelected) {
      // If any slot in the group is already selected, remove all groupSlotIds
      finalSelectedSlots = alreadySelected.filter((slotId) => !groupSlotIds.includes(slotId))
    } else {
      const conventionRoom = getConventionRoom(roomId)
      const weekdaySlots = conventionRoom.weekday_slots
      const weekendSlots = conventionRoom.weekend_slots

      // Calculate available slots based on whether this is a flexible room or not
      let remainedSlotsCount: number

      if (isFlexible) {
        const totalAvailableSlots = getTotalAvailableSlotsForFlexibleRooms()
        const totalUsedSlots = getTotalUsedSlotsForFlexibleRooms()
        remainedSlotsCount = totalAvailableSlots - totalUsedSlots
      } else {
        // For non-flexible rooms, calculate available slots for this specific room
        const availableSlotsCount = isWeekend ? weekendSlots : weekdaySlots + weekendSlots
        remainedSlotsCount = availableSlotsCount - alreadySelected.length
      }

      const unselectedSlots = groupSlotIds.filter((slotId) => !alreadySelected.includes(slotId))
      const additionalSlotsCount = Math.max(remainedSlotsCount, 0)

      finalSelectedSlots = [...alreadySelected, ...unselectedSlots.slice(0, additionalSlotsCount)]
    }

    setFieldValue(`slots.${roomId}`, finalSelectedSlots)
  }

  const getConventionRoom = (roomId: number) => {
    return conventionRooms.find((room) => room.id === roomId)!
  }

  const hasSelectedSlot = Object.keys(values.slots).some((key) => values.slots[key]?.length)

  useEffect(() => {
    if (open && date) {
      fetchRoomAvailability({
        bookingId,
        siteId,
        date,
      })
    }
  }, [open])

  function getIsWeekend() {
    const selectedDate = dayjs(date).startOf('day')
    const dayOfWeek = selectedDate.day()
    return dayOfWeek === 0 || dayOfWeek === 6
  }

  const isWeekend = getIsWeekend()

  const getTotalAvailableSlotsForFlexibleRooms = (): number => {
    if (!isFlexibleRooms || !conventionRooms.length) return 0

    const firstRoom = conventionRooms[0]
    return isWeekend ? firstRoom.weekend_slots : firstRoom.weekday_slots + firstRoom.weekend_slots
  }

  const getTotalUsedSlotsForFlexibleRooms = (): number => {
    if (!isFlexibleRooms) return 0

    return Object.entries(values.slots).reduce((total, [roomId, roomSlots]) => {
      // Only count slots from flexible rooms
      if (getIsFlexibleStatus(Number(roomId))) {
        const slots = roomSlots as number[] | undefined
        return total + (slots?.length || 0)
      }
      return total
    }, 0)
  }

  const checkHasReachedMaxSlots = (roomId: number): boolean => {
    const conventionRoom = getConventionRoom(roomId)
    const isFlexible = getIsFlexibleStatus(roomId)

    if (!conventionRoom) {
      return false
    }

    if (isFlexible) {
      // For flexible rooms, check against the total pool of flexible slots
      const totalAvailableSlots = getTotalAvailableSlotsForFlexibleRooms()
      const totalUsedSlots = getTotalUsedSlotsForFlexibleRooms()
      return totalUsedSlots >= totalAvailableSlots
    } else {
      // For non-flexible rooms, only check their own slots
      const weekendSlots = conventionRoom.weekend_slots
      const weekdaySlots = conventionRoom.weekday_slots

      // We allow to use weekend slots for weekday bookings, since weekend is more expensive
      const maximumSlots = isWeekend ? weekendSlots : weekdaySlots + weekendSlots
      const currentRoomSlots = values.slots[roomId]?.length || 0

      return currentRoomSlots >= maximumSlots
    }
  }

  const getDefaultSelectedSlots = (roomId: number) => {
    return values.slots[roomId] ?? []
  }

  function getHasRoomsUsedWeekendSlots() {
    return Object.keys(values.slots).some((roomId) => {
      const conventionRoom = getConventionRoom(Number(roomId))
      const selectedSlotsCount = values.slots[roomId]?.length || 0
      return selectedSlotsCount > conventionRoom.weekday_slots
    })
  }

  const onSubmitData = () => {
    const hasUsedWeekendSlots = !isWeekend && getHasRoomsUsedWeekendSlots()
    const isFlexibleAndUsingWeekend =
      isFlexibleRooms &&
      !isWeekend &&
      getTotalUsedSlotsForFlexibleRooms() > conventionRooms[0].weekday_slots

    if (hasUsedWeekendSlots || isFlexibleAndUsingWeekend) {
      Popup.showInfo(
        {
          title: 'Alert',
          message:
            'You are using weekend slots (more expensive) to book week day slots (less expensive).',
          mainButtonText: 'Book',
          secondButtonText: 'Cancel',
        },
        () => handleSubmit(),
      )
    } else {
      handleSubmit()
    }
  }

  return (
    <Modal
      footer={null}
      wrapClassName='promotion-room-availability-wrapper'
      destroyOnHidden
      onCancel={onCancel}
      open={open}
      title='I would like to book'>
      <Spin spinning={isSubmitting}>
        <div className='promotion-room-availability'>
          <Show>
            <Show.When isVisible={isFetching}>
              <RoomLoadingSkeleton skeletonRooms={skeletonRooms} />
            </Show.When>
            <Show.Else>
              {data?.map((room) => {
                return (
                  <RoomBookingSlots
                    key={room.id}
                    room={room}
                    handleSlotToggle={handleSlotToggle}
                    handleGroupToggle={handleGroupToggle}
                    selectedSlotIds={getDefaultSelectedSlots(room.id)}
                    checkHasReachedMaxSlots={() => checkHasReachedMaxSlots(room.id)}
                  />
                )
              })}
              <div className='footer-container'>
                <Button
                  disabled={isSubmitting || !hasSelectedSlot}
                  htmlType='submit'
                  onClick={onSubmitData}>
                  Add
                </Button>
              </div>
            </Show.Else>
          </Show>
        </div>
      </Spin>
    </Modal>
  )
}
